/**
 * Modular jigsaw piece factories:
 * - square: straight edges, unit cell [0,1]×[0,1]
 * - interlock: Bézier tabs/blanks, matching neighbors, same unit cell space
 */

import { hashSeed } from "./rng";

export { hashSeed } from "./rng";

/**
 * @param {number} rows
 * @param {number} cols
 * @returns {{ id:number, row:number, col:number, pathD:string, layout:'grid' }[]}
 */
export function createSquarePieces(rows, cols) {
  const pathD = "M 0 0 L 1 0 L 1 1 L 0 1 Z";
  const pieces = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      pieces.push({
        id: r * cols + c,
        row: r,
        col: c,
        pathD,
        layout: "grid",
      });
    }
  }
  return pieces;
}

function f(v) {
  return Math.round(v * 1e4) / 1e4;
}

/*
 * Classic jigsaw tab profile — narrow neck + round semicircular head.
 *
 * Each tab is 6 cubic Bézier segments arranged symmetrically about the
 * edge midpoint:
 *   1  edge → left neck       (smooth entry into narrow channel)
 *   2  left neck → head-left  (widens from neck to head)
 *   3  head-left → head-top   (left quarter-circle of head)
 *   4  head-top → head-right  (right quarter-circle of head)
 *   5  head-right → right neck (narrows from head to neck)
 *   6  right neck → edge      (smooth exit back to straight edge)
 *
 * Geom (w, d) is shared per joint so both neighbours trace the same curve.
 *
 * K = 0.5523 is the standard constant for a cubic Bézier quarter-circle.
 */

const K = 0.5523;

function tabParams(w, d) {
  /* Narrow neck + fuller head (classic jigsaw icon style). */
  const nk = w * 0.19;
  const nd = d * 0.28;
  const hr = d - nd;
  const hd = Math.min(hr, w * 0.5);
  const e = (1 - w) / 2;
  return { nk, nd, hr, hd, e };
}

/** Horizontal edge y = y0, left → right. tabUp: knob toward −y. */
function hEdgeLeftToRight(y0, tabUp, geom) {
  const { w, d } = geom;
  const s = tabUp ? -1 : 1;
  const { nk, nd, hr, hd, e } = tabParams(w, d);

  const xE = f(e);
  const xE2 = f(1 - e);
  const xNL = f(0.5 - nk);
  const xNR = f(0.5 + nk);
  const xHL = f(0.5 - hd);
  const xHR = f(0.5 + hd);
  const yB = f(y0);
  const yN5 = f(y0 + s * nd * 0.5);
  const yN = f(y0 + s * nd);
  const yN9 = f(y0 + s * nd * 0.95);
  const yHK = f(y0 + s * (nd + hr * K));
  const yHT = f(y0 + s * (nd + hr));

  return (
    ` L ${xE} ${yB}` +
    ` C ${f(e + w * 0.06)} ${yB} ${xNL} ${yB} ${xNL} ${yN5}` +
    ` C ${xNL} ${yN} ${xHL} ${yN9} ${xHL} ${yN}` +
    ` C ${xHL} ${yHK} ${f(0.5 - hd * K)} ${yHT} ${f(0.5)} ${yHT}` +
    ` C ${f(0.5 + hd * K)} ${yHT} ${xHR} ${yHK} ${xHR} ${yN}` +
    ` C ${xHR} ${yN9} ${xNR} ${yN} ${xNR} ${yN5}` +
    ` C ${xNR} ${yB} ${f(1 - e - w * 0.06)} ${yB} ${xE2} ${yB}` +
    ` L 1 ${yB}`
  );
}

/** Vertical x = x0, top → bottom. tabRight: knob toward +x. */
function vEdgeTopToBottom(x0, tabRight, geom) {
  const { w, d } = geom;
  const s = tabRight ? 1 : -1;
  const { nk, nd, hr, hd, e } = tabParams(w, d);

  const yE = f(e);
  const yE2 = f(1 - e);
  const yNL = f(0.5 - nk);
  const yNR = f(0.5 + nk);
  const yHL = f(0.5 - hd);
  const yHR = f(0.5 + hd);
  const xB = f(x0);
  const xN5 = f(x0 + s * nd * 0.5);
  const xN = f(x0 + s * nd);
  const xN9 = f(x0 + s * nd * 0.95);
  const xHK = f(x0 + s * (nd + hr * K));
  const xHT = f(x0 + s * (nd + hr));

  return (
    ` L ${xB} ${yE}` +
    ` C ${xB} ${f(e + w * 0.06)} ${xB} ${yNL} ${xN5} ${yNL}` +
    ` C ${xN} ${yNL} ${xN9} ${yHL} ${xN} ${yHL}` +
    ` C ${xHK} ${yHL} ${xHT} ${f(0.5 - hd * K)} ${xHT} ${f(0.5)}` +
    ` C ${xHT} ${f(0.5 + hd * K)} ${xHK} ${yHR} ${xN} ${yHR}` +
    ` C ${xN9} ${yHR} ${xN} ${yNR} ${xN5} ${yNR}` +
    ` C ${xB} ${yNR} ${xB} ${f(1 - e - w * 0.06)} ${xB} ${yE2}` +
    ` L ${xB} 1`
  );
}

/** Bottom y = y0, right → left. tabDown: knob toward +y. */
function hEdgeRightToLeft(y0, tabDown, geom) {
  const { w, d } = geom;
  const s = tabDown ? 1 : -1;
  const { nk, nd, hr, hd, e } = tabParams(w, d);

  const xE = f(e);
  const xE2 = f(1 - e);
  const xNL = f(0.5 - nk);
  const xNR = f(0.5 + nk);
  const xHL = f(0.5 - hd);
  const xHR = f(0.5 + hd);
  const yB = f(y0);
  const yN5 = f(y0 + s * nd * 0.5);
  const yN = f(y0 + s * nd);
  const yN9 = f(y0 + s * nd * 0.95);
  const yHK = f(y0 + s * (nd + hr * K));
  const yHT = f(y0 + s * (nd + hr));

  return (
    ` L ${xE2} ${yB}` +
    ` C ${f(1 - e - w * 0.06)} ${yB} ${xNR} ${yB} ${xNR} ${yN5}` +
    ` C ${xNR} ${yN} ${xHR} ${yN9} ${xHR} ${yN}` +
    ` C ${xHR} ${yHK} ${f(0.5 + hd * K)} ${yHT} ${f(0.5)} ${yHT}` +
    ` C ${f(0.5 - hd * K)} ${yHT} ${xHL} ${yHK} ${xHL} ${yN}` +
    ` C ${xHL} ${yN9} ${xNL} ${yN} ${xNL} ${yN5}` +
    ` C ${xNL} ${yB} ${f(e + w * 0.06)} ${yB} ${xE} ${yB}` +
    ` L 0 ${yB}`
  );
}

/** Left x = 0, bottom → top. tabLeft: knob toward −x. */
function vEdgeBottomToTop(x0, tabLeft, geom) {
  const { w, d } = geom;
  const s = tabLeft ? -1 : 1;
  const { nk, nd, hr, hd, e } = tabParams(w, d);

  const yE = f(e);
  const yE2 = f(1 - e);
  const yNL = f(0.5 - nk);
  const yNR = f(0.5 + nk);
  const yHL = f(0.5 - hd);
  const yHR = f(0.5 + hd);
  const xB = f(x0);
  const xN5 = f(x0 + s * nd * 0.5);
  const xN = f(x0 + s * nd);
  const xN9 = f(x0 + s * nd * 0.95);
  const xHK = f(x0 + s * (nd + hr * K));
  const xHT = f(x0 + s * (nd + hr));

  return (
    ` L ${xB} ${yE2}` +
    ` C ${xB} ${f(1 - e - w * 0.06)} ${xB} ${yNR} ${xN5} ${yNR}` +
    ` C ${xN} ${yNR} ${xN9} ${yHR} ${xN} ${yHR}` +
    ` C ${xHK} ${yHR} ${xHT} ${f(0.5 + hd * K)} ${xHT} ${f(0.5)}` +
    ` C ${xHT} ${f(0.5 - hd * K)} ${xHK} ${yHL} ${xN} ${yHL}` +
    ` C ${xN9} ${yHL} ${xN} ${yNL} ${xN5} ${yNL}` +
    ` C ${xB} ${yNL} ${xB} ${f(e + w * 0.06)} ${xB} ${yE}` +
    ` L ${xB} 0`
  );
}

/**
 * hOut[r][c]: +1 = upper piece has tab on its bottom; −1 = socket.
 * vOut[r][c]: +1 = left piece has tab on its right; −1 = socket.
 */
function buildKnobInterlockPath(r, c, rows, cols, hOut, vOut, hGeom, vGeom) {
  let d = "M 0 0";

  if (r === 0) {
    d += " L 1 0";
  } else {
    d += hEdgeLeftToRight(0, hOut[r - 1][c] === -1, hGeom[r - 1][c]);
  }

  if (c === cols - 1) {
    d += " L 1 1";
  } else {
    d += vEdgeTopToBottom(1, vOut[r][c] === 1, vGeom[r][c]);
  }

  if (r === rows - 1) {
    d += " L 0 1";
  } else {
    d += hEdgeRightToLeft(1, hOut[r][c] === 1, hGeom[r][c]);
  }

  if (c === 0) {
    d += " L 0 0";
  } else {
    d += vEdgeBottomToTop(0, vOut[r][c - 1] === -1, vGeom[r][c - 1]);
  }

  d += " Z";
  return d;
}

/**
 * Classic jigsaw: narrow-neck round-head knobs & matching sockets.
 * @returns {{ id:number, row:number, col:number, pathD:string, layout:'grid' }[]}
 */
const INTERLOCK_GEOM = Object.freeze({ w: 0.38, d: 0.26 });

export function createInterlockPieces(rows, cols, seed) {
  /**
   * Tab/socket layout: horizontal seams use (row+col); vertical seams use
   * (row+col+1), flipped per theme seed. That staggers H vs V so corners
   * alternate tab/socket like physical puzzles (not “same phase” on both axes).
   * Every internal edge still has exactly one tab and one mating socket.
   */
  const parityFlip = hashSeed([seed, rows, cols, "knob_v7_stagger"]) & 1;

  const hOut = [];
  for (let r = 0; r < Math.max(0, rows - 1); r++) {
    hOut[r] = [];
    for (let c = 0; c < cols; c++) {
      hOut[r][c] = (r + c + parityFlip) % 2 === 0 ? 1 : -1;
    }
  }

  const vOut = [];
  for (let r = 0; r < rows; r++) {
    vOut[r] = [];
    for (let c = 0; c < Math.max(0, cols - 1); c++) {
      vOut[r][c] = (r + c + 1 + parityFlip) % 2 === 0 ? 1 : -1;
    }
  }

  const hGeom = [];
  for (let r = 0; r < Math.max(0, rows - 1); r++) {
    hGeom[r] = [];
    for (let c = 0; c < cols; c++) {
      hGeom[r][c] = INTERLOCK_GEOM;
    }
  }

  const vGeom = [];
  for (let r = 0; r < rows; r++) {
    vGeom[r] = [];
    for (let c = 0; c < Math.max(0, cols - 1); c++) {
      vGeom[r][c] = INTERLOCK_GEOM;
    }
  }

  const pieces = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      pieces.push({
        id: r * cols + c,
        row: r,
        col: c,
        pathD: buildKnobInterlockPath(r, c, rows, cols, hOut, vOut, hGeom, vGeom),
        layout: "grid",
      });
    }
  }
  return pieces;
}
